import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import useTenantBranding from '../hooks/useTenantBranding';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { KeyRound, Loader2, Mail, Copy, ArrowLeft, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ForgotPassword() {
  const { branding } = useTenantBranding();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [demoUrl, setDemoUrl] = useState(null);
  const [message, setMessage] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return toast.error('Enter your email');
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/api/auth/forgot-password`, { email });
      setSubmitted(true);
      setMessage(data.message || 'Check your email.');
      if (data.demo_reset_url) setDemoUrl(data.demo_reset_url);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not send reset link');
    } finally {
      setLoading(false);
    }
  };

  const copyDemoUrl = async () => {
    if (!demoUrl) return;
    try {
      await navigator.clipboard.writeText(demoUrl);
      toast.success('Reset link copied');
    } catch {
      toast.error('Copy failed — long-press to copy manually');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-950 via-slate-900 to-sky-950 p-4">
      <Card className="w-full max-w-md" data-testid="forgot-password-page">
        <CardHeader className="text-center">
          {branding?.logo_url ? (
            <img
              src={branding.logo_url}
              alt={branding.name}
              className="w-14 h-14 rounded-2xl object-cover border border-white/10 mx-auto mb-3 shadow-lg"
              data-testid="forgot-brand-logo"
            />
          ) : (
            <div className="w-14 h-14 rounded-full bg-sky-500/15 border border-sky-500/30 mx-auto mb-3 flex items-center justify-center">
              <KeyRound className="w-7 h-7 text-sky-400" />
            </div>
          )}
          {branding && (
            <p className="text-sm text-sky-300 font-semibold mb-1">{branding.name}</p>
          )}
          <CardTitle>Forgot your password?</CardTitle>
          <CardDescription>
            Enter the email you signed up with. We'll send a reset link valid for 1 hour.
          </CardDescription>
        </CardHeader>

        {submitted ? (
          <CardContent className="space-y-4">
            <div className="flex items-start gap-3 rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-4 text-emerald-200">
              <CheckCircle className="h-5 w-5 text-emerald-400 mt-0.5 shrink-0" />
              <p className="text-sm">{message}</p>
            </div>

            {demoUrl && (
              <div className="rounded-xl border border-violet-500/30 bg-violet-500/10 p-4 space-y-2" data-testid="demo-reset-url">
                <p className="text-xs font-semibold text-violet-200 uppercase tracking-wider">Demo reset link</p>
                <p className="text-xs text-violet-200/80 break-all font-mono">{demoUrl}</p>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={copyDemoUrl}>
                    <Copy className="h-3 w-3 mr-1" /> Copy link
                  </Button>
                  <a href={demoUrl}>
                    <Button size="sm" className="btn-gradient-primary">Open</Button>
                  </a>
                </div>
              </div>
            )}

            <Link to="/login">
              <Button variant="outline" className="w-full">
                <ArrowLeft className="h-4 w-4 mr-1" /> Back to login
              </Button>
            </Link>
          </CardContent>
        ) : (
          <form onSubmit={submit}>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@business.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10"
                    required
                    disabled={loading}
                    data-testid="forgot-email-input"
                  />
                </div>
              </div>
              <Button type="submit" className="w-full btn-gradient-primary" disabled={loading} data-testid="forgot-submit">
                {loading ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Sending...</> : 'Send reset link'}
              </Button>
            </CardContent>
            <CardFooter className="justify-center">
              <Link to="/login" className="text-xs text-muted-foreground hover:underline">
                <ArrowLeft className="h-3 w-3 inline mr-1" /> Back to login
              </Link>
            </CardFooter>
          </form>
        )}
      </Card>
    </div>
  );
}
